import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import './Toast.css';

/*
 * Toast system — ported/expanded from the mock's single bottom-center toast.
 *
 * Wrap the app (or a subtree) once:
 *   <ToastProvider><App/></ToastProvider>
 * Then anywhere below:
 *   const toast = useToast();
 *   toast.success('Saved');  toast.error('Upload failed');  toast.info('…');
 *   toast('Plain message');            // shorthand -> success styling
 *   toast.show({ message, variant, duration });
 *
 * Toasts stack bottom-center and auto-dismiss (~2.5s default). Returns an id
 * you can pass to toast.dismiss(id).
 */

const ToastContext = createContext(null);

const VARIANT_ICON = {
  success: 'check',
  error: 'close',
  info: 'warning',
  warning: 'warning',
};

let idSeq = 0;

export function ToastProvider({ children, duration = 2500, max = 4 }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const tm = timers.current.get(id);
    if (tm) { clearTimeout(tm); timers.current.delete(id); }
  }, []);

  const show = useCallback((arg, maybeVariant) => {
    const opts = typeof arg === 'string' ? { message: arg, variant: maybeVariant } : (arg || {});
    const id = ++idSeq;
    const t = {
      id,
      message: opts.message,
      variant: opts.variant || 'success',
      duration: opts.duration != null ? opts.duration : duration,
    };
    setToasts((list) => {
      const next = [...list, t];
      return next.length > max ? next.slice(next.length - max) : next;
    });
    if (t.duration > 0) {
      const tm = setTimeout(() => dismiss(id), t.duration);
      timers.current.set(id, tm);
    }
    return id;
  }, [duration, max, dismiss]);

  // convenience API: toast(msg) callable + toast.success/error/info/warning
  const api = useMemo(() => {
    const fn = (msg, variant) => show(msg, variant);
    fn.show = show;
    fn.dismiss = dismiss;
    fn.success = (msg, o) => show({ message: msg, variant: 'success', ...(o || {}) });
    fn.error = (msg, o) => show({ message: msg, variant: 'error', ...(o || {}) });
    fn.info = (msg, o) => show({ message: msg, variant: 'info', ...(o || {}) });
    fn.warning = (msg, o) => show({ message: msg, variant: 'warning', ...(o || {}) });
    return fn;
  }, [show, dismiss]);

  // clear all timers on unmount
  useEffect(() => () => { timers.current.forEach(clearTimeout); timers.current.clear(); }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onDismiss }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="ui-toasts" role="region" aria-live="polite" aria-label="Notifications">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`ui-toast ui-toast--${t.variant} show`}
          role={t.variant === 'error' ? 'alert' : 'status'}
          onClick={() => onDismiss(t.id)}
        >
          <Icon name={VARIANT_ICON[t.variant] || 'check'} size={17} strokeWidth={2} className="ui-toast__icon" />
          <span className="ui-toast__msg">{t.message}</span>
        </div>
      ))}
    </div>,
    document.body
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a <ToastProvider>');
  }
  return ctx;
}

/*
 * Toast — standalone presentational toast (no provider). Handy for tests /
 * one-off placements. Prefer ToastProvider + useToast in the app.
 */
export default function Toast({ variant = 'success', message, children, onClick, className = '' }) {
  return (
    <div className={`ui-toast ui-toast--${variant} show${className ? ' ' + className : ''}`} role="status" onClick={onClick}>
      <Icon name={VARIANT_ICON[variant] || 'check'} size={17} strokeWidth={2} className="ui-toast__icon" />
      <span className="ui-toast__msg">{message != null ? message : children}</span>
    </div>
  );
}
